// src/hooks/useAdminData.ts
import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { useSession } from "./useSession";

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────

export interface AdminUser {
  clerk_user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: "super_admin" | "admin" | "user";
  is_active: boolean;
  last_login_at?: string;
  image_url?: string;
  teams?: AdminTeamRef[];
}

export interface AdminTeamRef {
  id: number;
  name: string;
  description?: string;
  added_at?: string;
}

export interface AdminTeam {
  id: number;
  name: string;
  description: string;
  created_by?: string;
  member_count: number;
  created_at?: string;
  updated_at?: string;
  members?: AdminTeamMember[];
}

export interface AdminTeamMember {
  clerk_user_id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  added_at?: string;
}

export interface AdminWorkspace {
  id: number;
  name: string;
  description: string;
  created_by?: string;
  team_count: number;
  resource_count: number;
  created_at?: string;
  updated_at?: string;
  teams?: { id: number; name: string; description?: string; added_at?: string }[];
  resources?: AdminResource[];
  available_teams?: { id: number; name: string }[];
}

export interface AdminResource {
  id: number;
  resource_type: string;
  resource_id: string;
  resource_name?: string;
  added_at?: string;
}

export interface AuditEntry {
  id: number;
  actor_label?: string | null;
  actor_email?: string | null;
  actor_first_name?: string | null;
  actor_last_name?: string | null;
  actor_role: "super_admin" | "admin" | "user";
  action: string;
  target_type?: string | null;
  target_id?: string | null;
  target_label?: string | null;
  details?: Record<string, any> | null;
  created_at: string;
}

export interface UserModuleAccess {
  dashboard: boolean;
  tariff_calculator: boolean;
  tariff_chat: boolean;
  risk_prediction: boolean;
  route_optimization: boolean;
  admin_panel: boolean;
}

type QueryOverrideOptions<TData, TKey extends readonly unknown[]> = Pick<
  UseQueryOptions<TData, Error, TData, TKey>,
  "enabled" | "staleTime" | "retry"
>;

// ──────────────────────────────────────────────
// Users
// ──────────────────────────────────────────────

const adminUsersQueryKey = ["admin", "users"] as const;

export function useAdminUsers(options?: QueryOverrideOptions<AdminUser[], typeof adminUsersQueryKey>) {
  const { isReady, isAuthenticated } = useSession();
  const isAuthEnabled = isReady && isAuthenticated;

  return useQuery<AdminUser[], Error, AdminUser[], typeof adminUsersQueryKey>({
    queryKey: adminUsersQueryKey,
    queryFn: async () => {
      const res = await apiService.getAdminUsers();
      return res.data.users;
    },
    enabled: isAuthEnabled && (options?.enabled ?? true),
    staleTime: options?.staleTime ?? 30_000,
    retry: options?.retry ?? 1,
  });
}

export interface AuditLogResponse {
  entries: AuditEntry[];
  total: number;
}

export function useAuditLog(
  limit: number = 10,
  page: number = 0,
  options?: QueryOverrideOptions<AuditLogResponse, readonly ["admin", "audit-log", number, number]>
) {
  const { isReady, isAuthenticated } = useSession();
  const isAuthEnabled = isReady && isAuthenticated;
  const offset = page * limit;
  const auditLogQueryKey = ["admin", "audit-log", limit, page] as const;
  return useQuery<AuditLogResponse, Error, AuditLogResponse, typeof auditLogQueryKey>({
    queryKey: auditLogQueryKey,
    queryFn: async () => {
      const res = await apiService.getAuditLog(limit, offset);
      return {
        entries: res.data.audit_log || [],
        total: res.data.total ?? 0,
      };
    },
    enabled: isAuthEnabled && (options?.enabled ?? true),
    staleTime: options?.staleTime ?? 30_000,
    retry: options?.retry ?? 1,
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clerkUserId, role }: { clerkUserId: string; role: string }) => {
      const res = await apiService.updateUserRole(clerkUserId, role);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (clerkUserId: string) => {
      const res = await apiService.deactivateUser(clerkUserId);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useActivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (clerkUserId: string) => {
      const res = await apiService.activateUser(clerkUserId);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useAddUserToTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clerkUserId, teamId }: { clerkUserId: string; teamId: number }) => {
      const res = await apiService.addUserToTeam(clerkUserId, teamId);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
    },
  });
}

export function useRemoveUserFromTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clerkUserId, teamId }: { clerkUserId: string; teamId: number }) => {
      const res = await apiService.removeUserFromTeam(clerkUserId, teamId);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
    },
  });
}

export function useGetUserModules() {
  return useMutation({
    mutationFn: async (clerkUserId: string) => {
      const res = await apiService.getUserModules(clerkUserId);
      return res.data as { clerk_user_id: string; role: string; modules: UserModuleAccess };
    },
  });
}

export function useSetUserModules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      clerkUserId,
      modules,
    }: {
      clerkUserId: string;
      modules: Partial<UserModuleAccess>;
    }) => {
      const res = await apiService.setUserModules(clerkUserId, modules);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "me", "modules"] });
    },
  });
}

// ──────────────────────────────────────────────
// Teams
// ──────────────────────────────────────────────

const adminTeamsQueryKey = ["admin", "teams"] as const;

export function useAdminTeams(options?: QueryOverrideOptions<AdminTeam[], typeof adminTeamsQueryKey>) {
  const { isReady, isAuthenticated } = useSession();
  const isAuthEnabled = isReady && isAuthenticated;

  return useQuery<AdminTeam[], Error, AdminTeam[], typeof adminTeamsQueryKey>({
    queryKey: adminTeamsQueryKey,
    queryFn: async () => {
      const res = await apiService.getAdminTeams();
      return res.data.teams;
    },
    enabled: isAuthEnabled && (options?.enabled ?? true),
    staleTime: options?.staleTime ?? 30_000,
    retry: options?.retry ?? 1,
  });
}

export function useAdminTeam(
  teamId: number | null,
  options?: QueryOverrideOptions<AdminTeam, readonly ["admin", "teams", number | null]>
) {
  const { isReady, isAuthenticated } = useSession();
  const isAuthEnabled = isReady && isAuthenticated;

  const adminTeamQueryKey = ["admin", "teams", teamId] as const;
  return useQuery<AdminTeam, Error, AdminTeam, typeof adminTeamQueryKey>({
    queryKey: adminTeamQueryKey,
    queryFn: async () => {
      const res = await apiService.getAdminTeam(teamId!);
      return res.data;
    },
    enabled: isAuthEnabled && teamId !== null && (options?.enabled ?? true),
    staleTime: options?.staleTime ?? 15_000,
    retry: options?.retry ?? 1,
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, description }: { name: string; description: string }) => {
      const res = await apiService.createAdminTeam(name, description);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
    },
  });
}

export function useUpdateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, name, description }: { teamId: number; name: string; description: string }) => {
      const res = await apiService.updateAdminTeam(teamId, name, description);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
    },
  });
}

export function useDeleteTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (teamId: number) => {
      const res = await apiService.deleteAdminTeam(teamId);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
    },
  });
}

export function useAddTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, clerkUserId }: { teamId: number; clerkUserId: string }) => {
      const res = await apiService.addTeamMember(teamId, clerkUserId);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, clerkUserId }: { teamId: number; clerkUserId: string }) => {
      const res = await apiService.removeTeamMember(teamId, clerkUserId);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ──────────────────────────────────────────────
// Workspaces
// ──────────────────────────────────────────────

const adminWorkspacesQueryKey = ["admin", "workspaces"] as const;

export function useAdminWorkspaces(
  options?: QueryOverrideOptions<AdminWorkspace[], typeof adminWorkspacesQueryKey>
) {
  const { isReady, isAuthenticated } = useSession();
  const isAuthEnabled = isReady && isAuthenticated;

  return useQuery<AdminWorkspace[], Error, AdminWorkspace[], typeof adminWorkspacesQueryKey>({
    queryKey: adminWorkspacesQueryKey,
    queryFn: async () => {
      const res = await apiService.getAdminWorkspaces();
      return res.data.workspaces;
    },
    enabled: isAuthEnabled && (options?.enabled ?? true),
    staleTime: options?.staleTime ?? 30_000,
    retry: options?.retry ?? 1,
  });
}

export function useAdminWorkspace(
  workspaceId: number | null,
  options?: QueryOverrideOptions<AdminWorkspace, readonly ["admin", "workspaces", number | null]>
) {
  const { isReady, isAuthenticated } = useSession();
  const isAuthEnabled = isReady && isAuthenticated;

  const adminWorkspaceQueryKey = ["admin", "workspaces", workspaceId] as const;
  return useQuery<AdminWorkspace, Error, AdminWorkspace, typeof adminWorkspaceQueryKey>({
    queryKey: adminWorkspaceQueryKey,
    queryFn: async () => {
      const res = await apiService.getAdminWorkspace(workspaceId!);
      return res.data;
    },
    enabled: isAuthEnabled && workspaceId !== null && (options?.enabled ?? true),
    staleTime: options?.staleTime ?? 15_000,
    retry: options?.retry ?? 1,
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, description }: { name: string; description: string }) => {
      const res = await apiService.createAdminWorkspace(name, description);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "workspaces"] });
    },
  });
}

export function useUpdateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceId, name, description }: { workspaceId: number; name: string; description: string }) => {
      const res = await apiService.updateAdminWorkspace(workspaceId, name, description);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "workspaces"] });
    },
  });
}

export function useDeleteWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (workspaceId: number) => {
      const res = await apiService.deleteAdminWorkspace(workspaceId);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "workspaces"] });
    },
  });
}

export function useAssignTeamToWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceId, teamId }: { workspaceId: number; teamId: number }) => {
      const res = await apiService.assignTeamToWorkspace(workspaceId, teamId);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "workspaces"] });
    },
  });
}

export function useRemoveTeamFromWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceId, teamId }: { workspaceId: number; teamId: number }) => {
      const res = await apiService.removeTeamFromWorkspace(workspaceId, teamId);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "workspaces"] });
    },
  });
}
